/**
 * Desktop table row dispatcher.
 */
import { handleFavoriteToggle } from './favoriteToggle';
import { handleDelete } from './deleteLot';
import { handleRefreshBid } from './refreshBid';
import { handleEditCloseTime } from './editCloseTime';
import { handleEditEstimate } from './editEstimate';
import { handleViewImages, handleViewDetails } from './viewMedia';
import { handlePriceApproval, handlePriceRetry } from './priceReviewHandlers';
import {
  handleMarkWon,
  handleMarkLost,
  handleMarkListed,
  handleMarkSold,
} from './lifecycleHandlers';
import { handleExportLot } from './exportHandlers';
import { getStartElement } from './getStartElement';
import type { TableHandlerDeps } from './types';

export function setupTableRowHandler(deps: TableHandlerDeps): void {
  const { els } = deps;

  if (!els.rows) return;

  els.rows.addEventListener(
    'click',
    (e: Event) => {
      const startEl = getStartElement(e);
      if (!startEl) return;

      const favoriteButton = startEl.closest('[data-action="favorite"]');
      if (favoriteButton) {
        const id = favoriteButton.getAttribute('data-id');
        if (id) {
          handleFavoriteToggle(id, e, deps);
        }
        return;
      }

      const deleteButton = startEl.closest('[data-action="delete"]');
      if (deleteButton) {
        const id = deleteButton.getAttribute('data-id');
        if (id) {
          handleDelete(id, e, deps);
        }
        return;
      }

      const refreshBidButton = startEl.closest('[data-action="refresh-bid"]');
      if (refreshBidButton) {
        const id = refreshBidButton.getAttribute('data-id');
        if (id) {
          handleRefreshBid(id, e, deps);
        }
        return;
      }

      const editCloseTimeButton = startEl.closest('[data-action="edit-close-time"]');
      if (editCloseTimeButton) {
        const id = editCloseTimeButton.getAttribute('data-id');
        if (id) {
          handleEditCloseTime(id, e, deps);
        }
        return;
      }

      const editEstimateButton = startEl.closest('[data-action="edit-estimate"]');
      if (editEstimateButton) {
        const id = editEstimateButton.getAttribute('data-id');
        if (id) {
          handleEditEstimate(id, e, deps);
        }
        return;
      }

      const viewImagesButton = startEl.closest('[data-action="view-images"]');
      if (viewImagesButton) {
        const id = viewImagesButton.getAttribute('data-id');
        if (id) {
          handleViewImages(id, e, deps);
        }
        return;
      }

      const deleteClosedButton = startEl.closest('[data-action="delete-closed"]');
      if (deleteClosedButton) {
        const id = deleteClosedButton.getAttribute('data-id');
        if (id) {
          handleDelete(id, e, deps);
        }
        return;
      }

      const lifecycleActions: Array<[string, (id: string, e: Event, d: TableHandlerDeps) => void]> =
        [
          ['mark-won', handleMarkWon],
          ['mark-lost', handleMarkLost],
          ['mark-listed', handleMarkListed],
          ['mark-sold', handleMarkSold],
          ['export-lot', handleExportLot],
        ];
      for (const [action, handler] of lifecycleActions) {
        const button = startEl.closest(`[data-action="${action}"]`);
        if (button) {
          const id = button.getAttribute('data-id');
          if (id) {
            handler(id, e, deps);
          }
          return;
        }
      }

      const detailsButton = startEl.closest('[data-action="view-details"]');
      if (detailsButton) {
        const id = detailsButton.getAttribute('data-id');
        if (id) {
          handleViewDetails(id, e, deps);
        }
        return;
      }

      const priceApproveButton = startEl.closest('[data-action="price-ack"]');
      if (priceApproveButton) {
        const id = priceApproveButton.getAttribute('data-id');
        if (id) {
          handlePriceApproval(id, e, deps);
        }
        return;
      }

      const priceCheckButton = startEl.closest('[data-action="price-retry"]');
      if (priceCheckButton) {
        const id = priceCheckButton.getAttribute('data-id');
        if (id) {
          handlePriceRetry(id, e, deps);
        }
        return;
      }
    },
    true
  );
}
